import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Info } from "lucide-react";

const ELIGIBLE_STATUSES = ["Assigned to Technician", "Vendor Assigned", "Rejected - Needs Review"];

const COST_ESTIMATE_ROLES = [
  "Technician",
  "Vendor",
  "SystemAdmin",
  "President",
  "District Manager",
  "District Operations",
  "Operations/DM", // legacy aliases
  "Operations/DO",
];

export function canSubmitCostEstimate(ticket, user) {
  if (!ticket || !user) return false;
  const role = user.role;
  if (!COST_ESTIMATE_ROLES.includes(role)) return false;
  return ELIGIBLE_STATUSES.includes(ticket.status);
}

export default function CostEstimateDialog({ ticket, open, onOpenChange, onSubmitted, adminEditMode = false }) {
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState({
    diagnosis: "",
    repair_plan: "",
    parts: [],
    labor_cost: 0,
    notes: "",
  });

  useEffect(() => {
    if (open && ticket) {
      const ce = ticket.cost_estimate;
      setEstimate({
        diagnosis: ce?.diagnosis || "",
        repair_plan: ce?.repair_plan || "",
        parts: ce?.parts ? ce.parts.map((p, i) => ({
          _cid: `p_init_${i}`,
          name: p.name,
          quantity: p.quantity,
          unit_cost: p.unit_cost,
        })) : [],
        labor_cost: ce?.labor_cost || 0,
        notes: ce?.notes || "",
      });
    }
  }, [open, ticket]);

  const addPart = () =>
    setEstimate(prev => ({
      ...prev,
      parts: [...prev.parts, { _cid: `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, name: "", quantity: 1, unit_cost: 0 }],
    }));

  const updatePart = (index, field, value) =>
    setEstimate(prev => {
      const newParts = [...prev.parts];
      newParts[index] = { ...newParts[index], [field]: value };
      return { ...prev, parts: newParts };
    });

  const removePart = (index) =>
    setEstimate(prev => ({
      ...prev,
      parts: prev.parts.filter((_, i) => i !== index),
    }));

  const total = () => {
    const partsTotal = estimate.parts.reduce(
      (sum, p) => sum + (Number(p.quantity) || 0) * (Number(p.unit_cost) || 0),
      0
    );
    return partsTotal + (parseFloat(estimate.labor_cost) || 0);
  };

  const submit = async () => {
    if (!estimate.diagnosis.trim() || !estimate.repair_plan.trim()) {
      toast.error("Please fill in diagnosis and repair plan");
      return;
    }
    // Validate: every part line must have quantity >= 1 and unit_cost >= 0
    for (let i = 0; i < estimate.parts.length; i++) {
      const p = estimate.parts[i];
      const qty = Number(p.quantity) || 0;
      const cost = Number(p.unit_cost) || 0;
      if (!p.name || !p.name.trim()) {
        toast.error(`Part #${i + 1}: please enter a part name or remove the row`);
        return;
      }
      if (qty < 1) {
        toast.error(`Part #${i + 1} ("${p.name}"): quantity must be at least 1. If you don't know, remove this part and leave the section empty — the technician will fill it in.`);
        return;
      }
      if (cost < 0) {
        toast.error(`Part #${i + 1} ("${p.name}"): unit cost cannot be negative`);
        return;
      }
    }
    // At least SOME dollar value must be present (parts total OR labor)
    const partsTotal = estimate.parts.reduce(
      (sum, p) => sum + (Number(p.quantity) || 0) * (Number(p.unit_cost) || 0),
      0
    );
    const laborTotal = parseFloat(estimate.labor_cost) || 0;
    if (partsTotal + laborTotal <= 0) {
      toast.error("Total estimate is $0. Either enter real costs/quantities, or if you don't know them, leave the whole section empty so the technician can complete it.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        diagnosis: estimate.diagnosis,
        repair_plan: estimate.repair_plan,
        parts: estimate.parts.map(({ _cid, ...rest }) => rest),
        labor_cost: parseFloat(estimate.labor_cost) || 0,
        notes: estimate.notes,
      };
      if (adminEditMode) {
        const res = await api.put(`/tickets/${ticket.id}/cost-estimate/admin-edit`, payload);
        toast.success(
          `Cost estimate updated. Approval chain reset — ${res.data?.cleared_approvals_count ?? 0} prior approvals cleared. New approval request sent.`,
          { duration: 7000 }
        );
      } else {
        await api.post(`/tickets/${ticket.id}/cost-estimate`, payload);
        toast.success("Cost estimate submitted for approval");
      }
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit estimate");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cost-estimate-dialog">
        <DialogHeader>
          <DialogTitle>
            {adminEditMode ? "Edit Cost Estimate (Admin)" : "Cost Estimate"}
            {ticket?.job_number ? ` - ${ticket.job_number}` : ""}
          </DialogTitle>
        </DialogHeader>

        {adminEditMode && (
          <div className="mx-6 mt-2 flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-300" data-testid="ce-admin-warning">
            <Info className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 leading-relaxed">
              <p className="font-semibold mb-1">You are editing an existing cost estimate.</p>
              <p>
                Saving will <strong>clear all previous approvals</strong> and restart the
                approval chain from the new total. A fresh approval email will be sent to the
                correct tier based on the updated amount.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4 py-4">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid="ce-info-banner">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900 leading-relaxed">
              <p className="font-semibold mb-1">Don't know the cost or quantity?</p>
              <p>
                If you are unsure about the repair cost or the number of items needed,
                <strong> leave the parts and labor section empty</strong>. The technician will fill it in during the on-site visit.
                <br />
                <span className="text-blue-800">Only add a line if you can provide BOTH a quantity (at least 1) and a unit cost.</span>
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Diagnosis *</Label>
            <Textarea
              placeholder="What is the root cause of the issue?"
              value={estimate.diagnosis}
              onChange={(e) => setEstimate(prev => ({ ...prev, diagnosis: e.target.value }))}
              rows={3}
              data-testid="ce-diagnosis-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Repair Plan *</Label>
            <Textarea
              placeholder="How will you fix the issue?"
              value={estimate.repair_plan}
              onChange={(e) => setEstimate(prev => ({ ...prev, repair_plan: e.target.value }))}
              rows={3}
              data-testid="ce-repair-plan-input"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Parts</Label>
              <Button type="button" variant="outline" size="sm" onClick={addPart} data-testid="ce-add-part-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Part
              </Button>
            </div>

            {estimate.parts.map((part, index) => (
              <div key={part._cid || `p_${index}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Input
                  placeholder="Part name"
                  value={part.name}
                  onChange={(e) => updatePart(index, "name", e.target.value)}
                  className="flex-1"
                  data-testid={`ce-part-name-${index}`}
                />
                <Input
                  type="number"
                  min="1"
                  placeholder="Qty"
                  value={part.quantity}
                  onChange={(e) => updatePart(index, "quantity", parseInt(e.target.value) || 0)}
                  className={`w-20 ${(Number(part.quantity) || 0) < 1 ? "border-red-400 focus:ring-red-400" : ""}`}
                  data-testid={`ce-part-qty-${index}`}
                />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Unit Cost"
                    value={part.unit_cost}
                    onChange={(e) => updatePart(index, "unit_cost", parseFloat(e.target.value) || 0)}
                    className="w-28 pl-7"
                  />
                </div>
                <span className="text-sm font-medium text-gray-700 w-20 text-right">
                  ${((Number(part.quantity) || 0) * (Number(part.unit_cost) || 0)).toFixed(2)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePart(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label>Labor Cost ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                step="0.01"
                value={estimate.labor_cost}
                onChange={(e) => setEstimate(prev => ({ ...prev, labor_cost: e.target.value }))}
                className="pl-7"
                data-testid="ce-labor-cost-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea
              placeholder="Any additional information..."
              value={estimate.notes}
              onChange={(e) => setEstimate(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
            />
          </div>

          <div className="p-4 bg-indigo-50 rounded-lg">
            <div className="flex justify-between items-center text-lg font-semibold">
              <span className="text-gray-700">Total Estimate</span>
              <span className="text-indigo-700" data-testid="ce-total">${total().toFixed(2)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={adminEditMode ? "bg-amber-600 hover:bg-amber-700" : "bg-indigo-600 hover:bg-indigo-700"}
            data-testid="ce-submit-btn"
          >
            {submitting
              ? (adminEditMode ? "Saving & resetting…" : "Submitting...")
              : (adminEditMode ? "Save & Reset Approvals" : "Submit for Approval")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
